import { ReactElement, useMemo } from 'react'
import { object } from 'yup'
import { Box, Stack } from '@mui/material'
import type { DefaultMemberCrossSectionsDTO } from '@api/fastAPI.schemas'
import { getDefaultValues } from '@editor/components/SingleElementCSForm/components/SingleCSForm/schema'
import {
  crossSectionFormDataToPayload,
  crossSectionSchema,
  type CrossSectionFormDataType,
} from '@editor/components/SingleElementCSForm/schema'
import { Form2, SubmitButton } from '@ui/forms'
import DefaultMemberCrossSectionForm, { type DefaultMember } from './DefaultMemberCrossSectionForm'

const sections: { member: DefaultMember; title: string }[] = [
  { member: 'purlins', title: 'Pfette' },
  { member: 'beams', title: 'Unterzug' },
  { member: 'columns', title: 'Stütze' },
]

const defaultMemberCrossSectionsFormSchema = object({
  purlins: crossSectionSchema.required(),
  beams: crossSectionSchema.required(),
  columns: crossSectionSchema.required(),
})

interface Props {
  values: DefaultMemberCrossSectionsDTO
  readOnly: boolean
  isSaving: boolean
  onSave: (body: DefaultMemberCrossSectionsDTO) => void
}

const DefaultMemberCrossSectionsPanel = ({
  values,
  readOnly,
  isSaving,
  onSave,
}: Props): ReactElement => {
  const defaultValues = useMemo(
    () => ({
      purlins: getDefaultValues(values.purlins as CrossSection).crossSection,
      beams: getDefaultValues(values.beams as CrossSection).crossSection,
      columns: getDefaultValues(values.columns as CrossSection).crossSection,
    }),
    [values],
  )

  return (
    <Form2
      id="default-cross-sections"
      data-cy="default-cross-sections"
      defaultValues={defaultValues}
      reinitialize
      validationSchema={defaultMemberCrossSectionsFormSchema}
      onSubmit={formValues => {
        onSave({
          purlins: crossSectionFormDataToPayload(formValues.purlins as CrossSectionFormDataType, {
            strict: true,
          }),
          beams: crossSectionFormDataToPayload(formValues.beams as CrossSectionFormDataType, {
            strict: true,
          }),
          columns: crossSectionFormDataToPayload(formValues.columns as CrossSectionFormDataType, {
            strict: true,
          }),
        } as DefaultMemberCrossSectionsDTO)
      }}
    >
      <Stack spacing={3}>
        {sections.map(section => (
          <DefaultMemberCrossSectionForm
            key={section.member}
            member={section.member}
            title={section.title}
            readOnly={readOnly}
          />
        ))}
        {!readOnly && (
          <Box>
            <SubmitButton
              loading={isSaving}
              fullWidth={false}
              data-cy="default-cross-sections-save"
            />
          </Box>
        )}
      </Stack>
    </Form2>
  )
}

export default DefaultMemberCrossSectionsPanel
